/**
 * Dispatch-stage history helpers: current pill status and hover preview text.
 */

import {
  BROADCAST_PREVIEW_MAX_CHARS,
  DRAFT_WITH_HISTORY_PILL,
  HISTORY_ONLY_STAGES,
  PILL_COLORS,
} from "./constants";

/**
 * True when the stage is history-only and must not become the card pill.
 *
 * @param {string} stageName Stage label.
 * @returns {boolean}
 */
export function isHistoryOnlyStage(stageName) {
  return HISTORY_ONLY_STAGES.has(stageName);
}

/**
 * Current lifecycle stage for the card pill (skips Broadcast).
 *
 * @param {Array<Object>} stageDetails Append-only dispatch_stage history.
 * @returns {string}
 */
export function getCurrentLifecycleStage(stageDetails) {
  if (!stageDetails?.length) {
    return "Draft";
  }
  for (let index = stageDetails.length - 1; index >= 0; index -= 1) {
    if (!isHistoryOnlyStage(stageDetails[index].stage)) {
      return stageDetails[index].stage;
    }
  }
  return "Draft";
}

/**
 * Index of the last lifecycle (non-Broadcast) history row, or -1 if none.
 *
 * @param {Array<Object>} stageDetails Append-only dispatch_stage history.
 * @returns {number}
 */
export function getLastLifecycleIndex(stageDetails) {
  if (!stageDetails?.length) {
    return -1;
  }
  for (let index = stageDetails.length - 1; index >= 0; index -= 1) {
    if (!isHistoryOnlyStage(stageDetails[index].stage)) {
      return index;
    }
  }
  return -1;
}

/**
 * Tailwind classes for the DayDetails status pill.
 * Draft with history uses a hover tint so the log is discoverable.
 *
 * @param {string} stage Lifecycle stage label.
 * @param {boolean} hasHistory Whether any dispatch_stage rows exist.
 * @returns {string}
 */
export function getStagePillClass(stage, hasHistory) {
  if (stage === "Draft" && hasHistory) {
    return DRAFT_WITH_HISTORY_PILL;
  }
  return PILL_COLORS[stage];
}

/**
 * Truncate a broadcast SMS preview for the hover log.
 * `additional_info.message` is the full body; `userIds` are stored for later use.
 * Shown in double quotes; truncated previews end with `..."`, then recipient count.
 *
 * @param {Object|null|undefined} additionalInfo Broadcast JSON payload.
 * @returns {string}
 */
export function getBroadcastMessagePreview(additionalInfo) {
  const message = additionalInfo?.message;
  if (!message) {
    return "";
  }
  const quoted =
    message.length <= BROADCAST_PREVIEW_MAX_CHARS
      ? `"${message}"`
      : `"${message.slice(0, BROADCAST_PREVIEW_MAX_CHARS)}..."`;
  const recipientCount = Array.isArray(additionalInfo?.userIds)
    ? additionalInfo.userIds.length
    : 0;
  const workerLabel = recipientCount === 1 ? "worker" : "workers";
  return `${quoted} - Sent to ${recipientCount} ${workerLabel}`;
}
